// Fail-soft source recovery helpers for structural syntax errors.
// Example: remove one unmatched `}` line and retry compile.
import { estimateMissingClosingBraces } from './fail-soft-braces'
import { extractErrorMessage, removeSourceLine, toSafeLineNumber } from './fail-soft-helpers'
import type { FailSoftRecoveryResult } from './fail-soft-types'

// Build a safe recovery source for known non-critical syntax failures.
/** @internal Build a safe recovery source for known non-critical syntax failures. */
export function buildFailSoftRecoverySource(source: string, error: unknown): FailSoftRecoveryResult | null {
  const errorMessage = extractErrorMessage(error)
  // Skip recovery when error payload has no message text.
  if (!errorMessage) {
    // Return null for empty error messages.
    return null
  } else {
    // Continue known syntax-recovery pattern checks.
  }

  // Regex for unmatched-closing-brace errors. Example: `unmatched closing brace at line 12`.
  const unmatchedMatch = errorMessage.match(/unmatched closing brace at line (\d+)/i)
  // Recover by dropping the unmatched-closure line.
  if (unmatchedMatch) {
    const line = toSafeLineNumber(unmatchedMatch[1])
    // Return recovery that removes the unmatched brace line.
    return {
      _source: removeSourceLine(source, line),
      _line: line,
      _message: `Fail-soft recovery removed unmatched closing brace line ${line}.`,
    }
  } else {
    // Continue missing-brace recovery checks.
  }

  // Regex for missing-closing-brace errors. Example: `missing closing brace for block opened near line 8`.
  const missingMatch = errorMessage.match(/missing closing brace for block opened near line (\d+)/i)
  // Recover by appending estimated missing closing braces.
  if (missingMatch) {
    const line = toSafeLineNumber(missingMatch[1])
    const missingBraceCount = Math.max(1, estimateMissingClosingBraces(source))
    const suffix = `\n${Array.from({ length: missingBraceCount }, () => '}').join('\n')}\n`
    // Return recovery that appends closing braces.
    return {
      _source: `${source}${suffix}`,
      _line: line,
      _message: `Fail-soft recovery appended ${missingBraceCount} closing brace(s) near line ${line}.`,
    }
  } else {
    // Return null for unsupported/critical syntax failures.
    return null
  }
}
